import { useRef, useState } from "react";
import { api, API, getUser, setUser } from "../lib/api";
import { openExternal } from "../lib/native";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ShieldCheck, ShieldAlert, ShieldX, Upload, Loader2, FileText, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

export default function CarrierLicenceCard({ onChanged }) {
  const user = getUser();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [license_no, setLicenseNo] = useState(user?.license_no || "");
  const [file, setFile] = useState(null);

  if (!user || user.role !== "carrier") return null;
  const status = user.license_status;

  // Approved state — small confirmation card
  if (status === "approved") {
    return (
      <Card data-testid="licence-approved" className="p-5 rounded-2xl border-[#4A6B56]/30 bg-[#4A6B56]/10 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#4A6B56] text-white flex items-center justify-center">
          <BadgeCheck className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>Licensed carrier verified</div>
          <div className="text-xs text-[#4A6B56]">Licence {user.license_no} · approved</div>
        </div>
      </Card>
    );
  }

  const submit = async (e) => {
    e?.preventDefault();
    if (!file || !license_no.trim()) {
      toast.error("Licence number and file are both required");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("license_no", license_no);
      form.append("user_id", user.id);
      await api.post(`/carriers/license`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // refresh user from server
      const { data: refreshed } = await api.get(`/auth/me/${user.id}`);
      setUser(refreshed);
      toast.success("Licence submitted — admin will verify shortly");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const banner = {
    pending: {
      icon: <ShieldAlert className="w-5 h-5" />,
      color: "bg-[#D67B54]/10 border-[#D67B54]/30",
      iconBg: "bg-[#D67B54] text-white",
      title: "Licence awaiting verification",
      body: "Our team usually reviews new carrier licences within 24 hours. You can't bid on jobs until you're approved.",
      cta: null,
    },
    rejected: {
      icon: <ShieldX className="w-5 h-5" />,
      color: "bg-red-50 border-red-200",
      iconBg: "bg-red-500 text-white",
      title: "Licence rejected",
      body: user.license_reject_reason || "Please re-upload a clearer copy.",
      cta: "Re-upload",
    },
    default: {
      icon: <ShieldCheck className="w-5 h-5" />,
      color: "bg-[#F6F4F0] border-[#E2DFD8]",
      iconBg: "bg-[#1B3624] text-white",
      title: "Upload your Waste Carrier Licence to start bidding",
      body: "We verify every carrier against the Environment Agency public register. Upload a PDF or photo of your licence and we'll review within 24h.",
      cta: "Upload licence",
    },
  };
  const b = banner[status] || banner.default;

  return (
    <Card data-testid="licence-card" className={`p-5 rounded-2xl border ${b.color}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${b.iconBg}`}>{b.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="font-medium" style={{ fontFamily: "Outfit" }}>{b.title}</div>
          <div className="text-sm text-[#6B6965] mt-1">{b.body}</div>
          {user.license_document_url && status !== "approved" && (
            <a
              href={`${API.replace(/\/api$/, "")}${user.license_document_url}`}
              onClick={(e) => {
                e.preventDefault();
                openExternal(`${API.replace(/\/api$/, "")}${user.license_document_url}`);
              }}
              data-testid="licence-current-doc"
              className="mt-2 inline-flex items-center gap-1 text-xs text-[#4A6B56] hover:underline"
            >
              <FileText className="w-3 h-3" /> View current submission
            </a>
          )}
        </div>
      </div>
      {(status === "pending" && !b.cta) ? null : (
        <form onSubmit={submit} className="mt-4 grid sm:grid-cols-[1fr_auto] gap-3 items-end">
          <div className="space-y-2">
            <Label className="text-xs">Licence number</Label>
            <Input
              data-testid="licence-no-input"
              value={license_no}
              onChange={(e) => setLicenseNo(e.target.value)}
              placeholder="WC-2024-XXXXX"
              className="rounded-xl bg-white"
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="licence-file-input"
            />
            <button
              type="button"
              data-testid="licence-file-trigger"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 text-sm text-[#1B3624] hover:underline"
            >
              <Upload className="w-4 h-4" />
              {file ? file.name : "Choose PDF or image…"}
            </button>
          </div>
          <Button
            type="submit"
            data-testid="licence-submit"
            disabled={uploading}
            className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white px-6"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : b.cta || "Submit"}
          </Button>
        </form>
      )}
    </Card>
  );
}
